import React from 'react';
import Icon from '../../../components/AppIcon';

const getDensity = (surveys, max) => {
  const ratio = max > 0 ? surveys / max : 0;
  if (ratio >= 0.75) return { label: 'Very High', color: 'bg-red-500' };
  if (ratio >= 0.5)  return { label: 'High',      color: 'bg-orange-500' };
  if (ratio >= 0.25) return { label: 'Medium',    color: 'bg-yellow-500' };
  return                     { label: 'Low',       color: 'bg-green-500' };
};

const GeographicHeatMap = ({ data = [], isLoading = false, className = '' }) => {
  const max = data.length ? Math.max(...data.map(r => r.surveys)) : 0;

  return (
    <div className={`bg-card rounded-lg border border-border p-6 ${className}`}>
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Map" size={20} color="var(--color-primary)" />
        <h3 className="text-lg font-semibold text-foreground">Geographic Distribution</h3>
        {isLoading && <span className="text-xs text-muted-foreground ml-auto">Loading...</span>}
      </div>

      {isLoading && data.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-14 bg-muted animate-pulse rounded-md" />
          ))}
        </div>
      ) : data.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No geographic data available</div>
      ) : (
        <>
          {/* Map */}
          <div className="relative w-full h-80 bg-muted rounded-lg overflow-hidden mb-6">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title="Property Survey Geographic Distribution"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=20.5937,78.9629&z=5&output=embed"
              className="rounded-lg"
            />
          </div>

          <div className="space-y-2">
            {data.map((region, idx) => {
              const density = getDensity(region.surveys, max);
              return (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-md">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full shrink-0 ${density.color}`} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{region.region}</p>
                      <p className="text-xs text-muted-foreground">{region.surveys} surveys · {density.label}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">₹{Number(region.avg_value).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Avg. Value</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            <h5 className="text-xs font-medium text-foreground mb-3">Density Legend</h5>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {[
                { color: 'bg-green-500',  label: 'Low' },
                { color: 'bg-yellow-500', label: 'Medium' },
                { color: 'bg-orange-500', label: 'High' },
                { color: 'bg-red-500',    label: 'Very High' },
              ].map(({ color, label }) => (
                <div key={label} className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${color}`} />
                  <span className="text-xs text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GeographicHeatMap;
